import type { Store } from '../types';
import type { WorkflowGraph } from '../../graph';
import { insertStep } from '../../commands/insertStepCommand';
import type { InsertStepPayload, InsertStepResult } from '../../commands/insertStepCommand';
import { pushUndo } from './historyActions';
import { cloneGraphData } from './snapshots';
import { propagateSimpleComposition } from '../compositionService';
import { withSyncGuard } from '../sync/syncFlag';

type SetState = (partial: Partial<Store> | ((state: Store) => Partial<Store>)) => void;
type GetState = () => Store;

export function createInsertActions(
  set: SetState,
  get: GetState,
  getGraph: () => WorkflowGraph | null | undefined,
) {
  return {
    insertStep(payload: InsertStepPayload): InsertStepResult {
      pushUndo(get, set, `Insert step: ${payload.displayName}`);

      // Step 1: compute what needs to be created
      const clone = cloneGraphData(get().graphData);
      const result = insertStep(clone, payload);

      // Step 2: apply composition stamping on the clone
      const anchorNode = clone.materialNodes[payload.anchorMaterialId];
      for (const newMatId of result.newMaterialIds) {
        const node = clone.materialNodes[newMatId];
        if (!node || node.composition) continue;
        if (anchorNode) {
          node.composition = propagateSimpleComposition(
            payload.anchorMaterialId,
            anchorNode.name,
            result.operationId,
          );
        }
      }

      // Step 3: apply new material nodes through individual action
      const currentMats = get().graphData.materialNodes;
      for (const matId of result.newMaterialIds) {
        if (!currentMats[matId]) {
          const spec = clone.materialNodes[matId];
          if (spec) get().addMaterialNode(spec);
        }
      }

      // Step 4: apply operation nodes (new + updated creationOrder)
      const currentOps = get().graphData.operationNodes;
      
      // First, update creationOrder for all existing operations that changed
      for (const [opId, cloneOp] of Object.entries(clone.operationNodes)) {
        const currentOp = currentOps[opId];
        if (currentOp && currentOp.creationOrder !== cloneOp.creationOrder) {
          // This operation had its creationOrder shifted by insertStep()
          get().updateOperationNode(opId, { creationOrder: cloneOp.creationOrder });
        }
      }
      
      // Then, add the new operation node
      if (!currentOps[result.operationId]) {
        const opSpec = clone.operationNodes[result.operationId];
        if (opSpec) get().addOperationNode(opSpec);
      }

      // Step 5: apply new flow edges through individual actions
      // Compare clone edges vs current store edges — apply only new ones
      const currentEdges = get().graphData.flowEdges;
      for (const [edgeId, edge] of Object.entries(clone.flowEdges)) {
        if (!currentEdges[edgeId]) {
          // Determine direction and call the right action
          const sourceIsOp = !!clone.operationNodes[edge.sourceId];
          if (sourceIsOp) {
            get().connectOperationToMaterial(edge.sourceId, edge.targetId);
          } else {
            get().connectMaterialToOperation(edge.sourceId, edge.targetId);
          }
        }
      }

      // Step 5b: splice in-line. The command rerouted the anchor's existing
      // downstream edges onto the new post-state material (in `clone`), but
      // those edges already exist in the store, so Step 5 skipped them. Detect
      // the rerouted edges (same id, changed sourceId) and apply the new source
      // to BOTH the store and the visual op-edge — otherwise the old downstream
      // stays on the anchor and the step forks instead of splicing.
      const currentEdgesForRewire = get().graphData.flowEdges;
      const rewires: Array<{ edgeId: string; oldSource: string; newSource: string }> = [];
      for (const [edgeId, edge] of Object.entries(clone.flowEdges)) {
        const cur = currentEdgesForRewire[edgeId];
        if (cur && cur.sourceId !== edge.sourceId) {
          rewires.push({ edgeId, oldSource: cur.sourceId, newSource: edge.sourceId });
        }
      }
      if (rewires.length > 0) {
        set((state) => {
          const flowEdges = { ...state.graphData.flowEdges };
          for (const r of rewires) {
            flowEdges[r.edgeId] = { ...flowEdges[r.edgeId], sourceId: r.newSource };
          }
          return { graphData: { ...state.graphData, flowEdges } };
        });
        withSyncGuard(set, () => {
          const g = getGraph();
          for (const r of rewires) {
            g?.rewireFlowEdgeSource(r.edgeId, r.oldSource, r.newSource);
          }
        });
      }

      // Step 6: bring the visual graph to parity with the snapshot path.
      const opId = result.operationId;

      // TRANSFER (2-in/2-out) cannot be expressed by the 1-in/1-out
      // pending-half collapse; rebuild the remainder/transfer/dest edges the
      // same way hydrateFromSnapshot does (Step D2 in snapshot.ts).
      if (payload.archetype === 'TRANSFER') {
        const inputs = Object.values(clone.flowEdges).filter((e) => e.targetId === opId);
        const outputs = Object.values(clone.flowEdges).filter((e) => e.sourceId === opId);
        const srcPre = inputs[0]?.sourceId ?? null;
        const dstPre = inputs.length >= 2 ? inputs[1].sourceId : null;
        const srcPost = outputs[0]?.targetId ?? null;
        const dstPost = outputs.length >= 2 ? outputs[1].targetId : null;
        if (srcPre && srcPost && dstPost) {
          withSyncGuard(set, () => {
            getGraph()?.addTransferVisualEdges({
              operationId: opId,
              srcPre,
              dstPre,
              srcPost,
              dstPost,
              displayName: payload.displayName,
            });
          });
        }
      }

      // If the anchor was inside a group, reparent the new op + its I/O
      // materials into the swimlane (collectMemberCells resolves membership).
      const groupId = clone.operationNodes[opId]?.groupId;
      if (groupId) {
        withSyncGuard(set, () => {
          getGraph()?.addStepsToGroup(groupId, [opId]);
        });
      }

      // Run a single hierarchical layout pass over the now-complete graph so
      // the result matches a fresh snapshot render (the incremental adds above
      // each triggered a partial pass; this guarantees the clean final layout).
      withSyncGuard(set, () => {
        getGraph()?.triggerLayout();
      });

      set({ ui: { ...get().ui, isDirty: true } });
      return result;
    },
  };
}
